import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Button } from "@mui/material";
import { Info } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { toDisplayTime } from "../../utilis/date-formats";

const MultipleFacilitiesPauseDateDialog = ({
	open,
	onClose,
	pauseDate,
	facilityPauseDates
}) => {
	const facilities = useSelector((state) => state.facility);
	const formatDate = (dateString) => {
		if (!dateString) return "";
		return toDisplayTime(dateString, "MMMM D, YYYY");
	};

	const getFacilityNamesWithPauseDates = () => {
		return facilityPauseDates
			?.filter(facility => facility.pauseDate)
			?.map(facility => ({
				name: facility.facilityName || `Facility ${facility.facilityId}`,
				pauseDate: facility.pauseDate
			})) || [];
	};

	const facilitiesWithPauseDates = getFacilityNamesWithPauseDates();

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				style: {
					borderRadius: '12px',
					boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
				}
			}}
		>
			<DialogTitle
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					pb: 1,
					borderBottom: '1px solid #e0e0e0'
				}}
			>
				<Info sx={{ color: '#1976d2', fontSize: 24 }} />
				<Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
					Facilities Pause Date Detected
				</Typography>
			</DialogTitle>

			<DialogContent sx={{ pt: 3, pb: 2 }}>
				<Box sx={{ mb: 3 }}>
					<Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
						We detected pause dates across facilities in your account. To ensure consistent data analysis, 
						we have automatically selected the earliest pause date as the effective pause date for your dashboard.
					</Typography>
					
					<Box sx={{ 
						backgroundColor: '#f8f9fa', 
						border: '1px solid #e3f2fd', 
						borderRadius: '8px', 
						p: 2,
						mb: 3 
					}}>
						<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1976d2' }}>
							Effective Pause Date: {formatDate(pauseDate)}
						</Typography>
						<Typography variant="body2" color="text.secondary">
							This date will be used to limit data visibility across all your selected facilities.
						</Typography>
					</Box>
				</Box>

				{facilitiesWithPauseDates.length > 0 && (
					<Box>
						<Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
							Facilities with Pause Dates:
						</Typography>
						<Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
							{facilitiesWithPauseDates.map((facility, index) => (
								<Box 
									key={index} 
									sx={{ 
										display: 'flex', 
										justifyContent: 'space-between', 
										alignItems: 'center',
										p: 1.5,
										mb: 1,
										backgroundColor: '#fafafa',
										borderRadius: '6px',
										border: '1px solid #e0e0e0'
									}}
								>
									<Typography variant="body2" sx={{ fontWeight: 500 }}>
										{facilities.find(f => f.facilityId?.toString() === facility.facilityId?.toString())?.name || facility.name}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{formatDate(facility.pauseDate)}
									</Typography>
								</Box>
							))}
						</Box>
					</Box>
				)}				
			</DialogContent>

			<DialogActions sx={{ p: 3, pt: 1 }}>
				<Button
					onClick={onClose}
					variant="contained"
					color="primary"
					size="medium"
				>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default MultipleFacilitiesPauseDateDialog;
